import { useQuery } from "@tanstack/react-query";
import {
  addDays,
  format,
  isBefore,
  isToday,
  isFuture,
  parse,
  startOfDay,
} from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { parseLocalDate } from "@/lib/date";
import { getActiveLocal, getAllItems, setActiveLocal } from "@/features/offline/db";
import type { ActiveCheckIn, Activation, StaffShift, UnsubmittedShift } from "./types";

export const checkinKeys = {
  all: ["checkin"] as const,
  active: (staffId: string) => [...checkinKeys.all, "active", staffId] as const,
  shifts: (staffId: string) => [...checkinKeys.all, "shifts", staffId] as const,
  activations: () => [...checkinKeys.all, "activations"] as const,
  unsubmitted: (staffId: string) => [...checkinKeys.all, "unsubmitted", staffId] as const,
  myWorkspaces: () => [...checkinKeys.all, "my-workspaces"] as const,
};

export type StaffWorkspace = { id: string; name: string };

/**
 * Workspaces the signed-in staffer belongs to as a BA (rpc my_workspaces,
 * role = 'staff'). Used only to decide whether activation cards need a
 * workspace label: one workspace → no label, more than one → show it.
 */
export function useMyStaffWorkspaces() {
  return useQuery({
    queryKey: checkinKeys.myWorkspaces(),
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<StaffWorkspace[]> => {
      const { data, error } = await supabase.rpc("my_workspaces");
      if (error) throw error;
      return (data ?? [])
        .filter((w) => w.role === "staff")
        .map((w) => ({ id: w.id, name: w.name }));
    },
  });
}

/** A scheduled shift plus the workspace that owns its activation. */
export type StaffShiftWithWorkspace = StaffShift & { workspace_id: string | null };

/**
 * Shifts the staffer checked out of but hasn't reviewed/submitted yet: a
 * 'completed' check-in whose auto-created time_log is still pending. Surfaced on
 * Home so "I'll submit later" is real — the hours are already captured (they'll
 * show as Pending), but the staffer can still adjust and finalize after a reload.
 */
export function useUnsubmittedShifts(staffId: string | undefined) {
  return useQuery({
    queryKey: staffId ? checkinKeys.unsubmitted(staffId) : ["checkin", "unsubmitted", null],
    enabled: Boolean(staffId),
    queryFn: async (): Promise<UnsubmittedShift[]> => {
      const { data, error } = await supabase
        .from("check_ins")
        .select(
          "id, activation_id, organization_id, check_in_at, check_out_at, time_log_id, activations(name), time_logs:time_log_id(hours, notes, status)",
        )
        .eq("staff_id", staffId!)
        .eq("status", "completed")
        .not("time_log_id", "is", null)
        .order("check_out_at", { ascending: false });
      if (error) throw error;
      type Row = {
        id: string;
        activation_id: string;
        organization_id: string | null;
        check_in_at: string;
        check_out_at: string | null;
        activations: { name: string } | null;
        time_logs: { hours: number; notes: string | null; status: string } | null;
      };
      return ((data ?? []) as unknown as Row[])
        .filter((r) => r.time_logs?.status === "pending")
        .map((r) => ({
          checkInId: r.id,
          activationId: r.activation_id,
          organizationId: r.organization_id,
          activationName: r.activations?.name ?? "Shift",
          checkInAt: r.check_in_at,
          checkOutAt: r.check_out_at,
          hours: Number(r.time_logs?.hours ?? 0),
          notes: r.time_logs?.notes ?? "",
        }));
    },
  });
}

export function useActiveCheckIn(staffId: string | undefined) {
  return useQuery({
    queryKey: staffId ? checkinKeys.active(staffId) : ["checkin", "active", null],
    enabled: Boolean(staffId),
    // Run even offline so we can return the persisted active check-in (the
    // running shift timer) rather than being paused with no data.
    networkMode: "always",
    queryFn: async (): Promise<ActiveCheckIn | null> => {
      const local = (await getActiveLocal(staffId!)) as ActiveCheckIn | null;

      // Trust the server as source of truth only when we're online AND the
      // offline queue is empty. Otherwise the local copy is ahead of (or all
      // we have of) the truth — an offline check-in not yet synced, or a
      // reconnect before the queue has drained.
      const items = await getAllItems();
      const hasPending = items.some((i) => !i.failed);

      if (navigator.onLine && !hasPending) {
        try {
          const { data, error } = await supabase
            .from("check_ins")
            .select(
              "id, activation_id, check_in_at, check_in_lat, check_in_lng, break_start_at, total_break_minutes, organization_id, activations(name, location)",
            )
            .eq("staff_id", staffId!)
            .in("status", ["active", "on_break"])
            .maybeSingle();
          if (error) throw error;
          const server = data as unknown as ActiveCheckIn | null;
          await setActiveLocal(staffId!, server); // mirror server truth locally
          return server;
        } catch {
          return local; // network blip — fall back to what we have
        }
      }

      return local;
    },
  });
}

export type ShiftTimeRow = {
  id: string;
  label: string | null;
  start_time: string;
  end_time: string;
  /** "YYYY-MM-DD" for a single-day shift; null means every day of the activation. */
  shift_date: string | null;
};

export type ExpandableActivation = {
  id: string;
  name: string;
  location: string | null;
  start_date: string | null;
  end_date: string | null;
  activation_shift_times: ShiftTimeRow[] | null;
};

/**
 * Pure expansion of one activation's shift times into discrete StaffShift rows.
 *
 *   • A shift time with `shift_date` set appears ONLY on that date (and only if
 *     the date is inside the activation's start..end range).
 *   • A shift time with `shift_date` NULL repeats on every day of the range.
 *   • Days before `today` are skipped.
 *   • `isVisible(shiftTimeId)` applies the per-staff opt-in rule
 *     (staff_shift_assignments) — the caller decides, this helper just filters.
 *
 * Output is ordered by date, then start time.
 */
export function expandShiftTimes(
  act: ExpandableActivation,
  today: Date,
  isVisible: (shiftTimeId: string) => boolean = () => true,
): StaffShift[] {
  if (!act.start_date || !act.end_date) return [];
  const shiftTimes = (act.activation_shift_times ?? []).filter((st) => isVisible(st.id));
  if (shiftTimes.length === 0) return [];

  const rangeStart = parseLocalDate(act.start_date);
  const rangeEnd = parseLocalDate(act.end_date);
  const todayStart = startOfDay(today);

  const dated = shiftTimes.filter((st) => st.shift_date !== null);
  const undated = shiftTimes.filter((st) => st.shift_date === null);

  const shifts: StaffShift[] = [];
  const push = (st: ShiftTimeRow, dayStr: string) => {
    shifts.push({
      id: `${act.id}:${st.id}:${dayStr}`,
      activation_id: act.id,
      shift_date: dayStr,
      start_time: st.start_time,
      end_time: st.end_time,
      title: st.label ? `${act.name} — ${st.label}` : act.name,
      location: act.location,
      status: "scheduled",
    });
  };

  for (let day = rangeStart; !isBefore(rangeEnd, day); day = addDays(day, 1)) {
    if (isBefore(day, todayStart)) continue; // skip past dates
    const dayStr = format(day, "yyyy-MM-dd");
    for (const st of undated) push(st, dayStr);
    for (const st of dated) {
      if (st.shift_date!.slice(0, 10) === dayStr) push(st, dayStr);
    }
  }

  shifts.sort((a, b) => {
    const dateCmp = a.shift_date.localeCompare(b.shift_date);
    if (dateCmp !== 0) return dateCmp;
    return a.start_time.localeCompare(b.start_time);
  });
  return shifts;
}

/**
 * Returns the shifts a staff member is scheduled for, synthesized from:
 *   staff_event_assignments × activations.start_date..end_date × activation_shift_times
 *
 * Data model note: the app has no concept of "one staff member's specific shift
 * instance." Instead, a staff member is assigned to an activation (gig/event),
 * and the activation has a set of shift times. A shift time either carries a
 * `shift_date` (it happens on that one day — this is what admin bulk-create
 * writes, one row per day) or has `shift_date` NULL (it repeats daily across
 * the activation's date range). We expand that into discrete StaffShift rows
 * via `expandShiftTimes` so the home page and check-in eligibility logic can
 * treat them uniformly.
 *
 * Per-shift scoping (see `staff_shift_assignments` migration):
 *   • If a shift_time has ZERO rows in `staff_shift_assignments`, it's visible
 *     to every activation-roster member (back-compat default).
 *   • If it has one or more rows, ONLY the listed staff see it.
 *   We fetch a lightweight companion query for the current staff's explicit
 *   assignments and the set of shift_time_ids that have any assignment at all,
 *   then apply the rule client-side while expanding the date grid.
 *
 * Past dates are filtered out; today and future only.
 */
export function useTodayShifts(staffId: string | undefined) {
  return useQuery({
    queryKey: staffId ? checkinKeys.shifts(staffId) : ["checkin", "shifts", null],
    enabled: Boolean(staffId),
    queryFn: async (): Promise<StaffShiftWithWorkspace[]> => {
      const [rosterRes, myShiftRes] = await Promise.all([
        // `activations!inner`: since workspace-scoped visibility (Phase B) an
        // assignment can point at an activation the staffer can no longer
        // read. The inner join drops those rows server-side, and the
        // `!row.activations` guard below covers the same case client-side.
        supabase
          .from("staff_event_assignments")
          .select(
            `
            id,
            activation_id,
            activations!inner (
              id,
              name,
              location,
              start_date,
              end_date,
              workspace_id,
              activation_shift_times ( id, label, start_time, end_time, shift_date )
            )
          `,
          )
          .eq("staff_id", staffId!),
        // Every per-shift assignment for THIS staff member. Small query — a
        // staff member is realistically assigned to a handful of shifts at
        // any given time.
        supabase
          .from("staff_shift_assignments")
          .select("shift_time_id")
          .eq("staff_id", staffId!),
      ]);
      if (rosterRes.error) throw rosterRes.error;
      if (myShiftRes.error) throw myShiftRes.error;

      type Row = {
        id: string;
        activation_id: string;
        activations: (ExpandableActivation & { workspace_id: string | null }) | null;
      };

      const rows = (rosterRes.data ?? []) as unknown as Row[];
      const myShiftIds = new Set(
        (myShiftRes.data ?? []).map((r) => r.shift_time_id),
      );

      // To apply the "opt-in" rule we need to know which shift_time_ids have
      // ANY assignment at all (not just ours). RLS lets us read our own
      // assignment rows — for others' rows on a shift we're not on, we need
      // a separate lookup. Fetch the set of "shift_times that have at least
      // one explicit assignment" for the activations we're on, using the
      // admin RLS path? We can't — staff RLS only shows own rows. So we
      // take a different approach: for each shift_time, check if *anyone*
      // has it via a count. But that's also RLS-blocked.
      //
      // Simpler working solution: expose a small SECURITY DEFINER function
      // that returns the set of shift_time_ids with any assignments. But
      // we'd rather not add server complexity.
      //
      // Pragmatic alternative: exploit the structure. The admin UI only
      // creates assignments for shifts where the admin wants to restrict.
      // Staff see a shift iff (a) nobody has it assigned — we don't know
      // this from staff RLS — OR (b) they themselves have it assigned.
      //
      // To keep the staff client honest without a DB helper, we change the
      // staff_shift_assignments SELECT policy to "any authenticated user
      // can SELECT shift_time_id + staff_id for the activations they're on"
      // via an EXISTS on staff_event_assignments. See the migration note.
      //
      // For this query, we collect the set of shift_time_ids that have any
      // assignment rows we can see; under the updated RLS, that means every
      // assignment on every shift we can reach (i.e. on activations we're
      // on the roster for). Anything outside that set is "everyone" (or
      // empty), which means we — being on the roster — see it.
      const anyAssignmentShiftIds = new Set<string>();
      {
        const activationIds = rows
          .map((r) => r.activations?.id)
          .filter((v): v is string => Boolean(v));
        if (activationIds.length > 0) {
          const { data: reachable, error: reachErr } = await supabase
            .from("staff_shift_assignments")
            .select(
              "shift_time_id, activation_shift_times!inner(activation_id)",
            )
            .in("activation_shift_times.activation_id", activationIds);
          // Swallow errors here — if RLS blocks it, fall back to the
          // "everyone sees everything" legacy behavior. Better to over-show
          // than under-show and confuse staff waiting for a shift to appear.
          if (!reachErr) {
            for (const r of reachable ?? []) {
              anyAssignmentShiftIds.add(
                (r as { shift_time_id: string }).shift_time_id,
              );
            }
          }
        }
      }

      const today = startOfDay(new Date());
      // Opt-in filter: if a shift_time has any explicit assignments and
      // we're not one of them, hide it.
      const isVisible = (shiftTimeId: string) =>
        !anyAssignmentShiftIds.has(shiftTimeId) || myShiftIds.has(shiftTimeId);

      const shifts: StaffShiftWithWorkspace[] = [];
      for (const row of rows) {
        // Activation hidden by RLS (not in one of our workspaces, not
        // explicitly assigned) — skip, never crash.
        if (!row.activations) continue;
        const workspaceId = row.activations.workspace_id ?? null;
        for (const s of expandShiftTimes(row.activations, today, isVisible)) {
          shifts.push({ ...s, workspace_id: workspaceId });
        }
      }

      shifts.sort((a, b) => {
        const dateCmp = a.shift_date.localeCompare(b.shift_date);
        if (dateCmp !== 0) return dateCmp;
        return a.start_time.localeCompare(b.start_time);
      });
      return shifts;
    },
  });
}

export function useActivations() {
  return useQuery({
    queryKey: checkinKeys.activations(),
    queryFn: async (): Promise<Activation[]> => {
      const { data, error } = await supabase
        .from("activations")
        .select(
          "id, name, location, address, latitude, longitude, geofence_radius_meters, start_date, end_date, require_checkin_photo",
        )
        .order("start_date", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Activation[];
    },
  });
}

const ONE_HOUR_MS = 60 * 60 * 1000;

export function useEligibleActivations(staffId: string | undefined) {
  const shiftsQ = useTodayShifts(staffId);
  const actsQ = useActivations();

  const shifts = shiftsQ.data ?? [];
  const activations = actsQ.data ?? [];

  // Match by activation_id directly. The legacy approach matched by title,
  // but titles are now synthesized as "${activation.name} — ${label}" and no
  // longer equal the activation name.
  const eligibleActivationIds = new Set(
    shifts
      .filter((s) => {
        // s.shift_date is "YYYY-MM-DD" — parseLocalDate keeps it as
        // local-midnight so isToday() agrees with the user's clock.
        const day = parseLocalDate(s.shift_date);
        if (isToday(day)) return true;
        if (!isFuture(startOfDay(day))) return false;
        const start = parse(s.start_time.slice(0, 5), "HH:mm", day);
        return start.getTime() - Date.now() <= ONE_HOUR_MS;
      })
      .map((s) => s.activation_id),
  );

  const eligible = activations.filter((a) => eligibleActivationIds.has(a.id));

  return {
    eligible,
    activations,
    shifts,
    isLoading: shiftsQ.isLoading || actsQ.isLoading,
    error: shiftsQ.error ?? actsQ.error,
  };
}
